import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { CTASection } from "@/components/CTASection";
import { Reveal } from "@/components/Reveal";
import { BIZ } from "@/lib/content";
import { useSeo } from "@/lib/useSeo";
import { Phone, Mail, MapPin, Clock, Instagram, Facebook, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INTERESTS = ["Sports Performance", "Baseball Instruction", "Softball Instruction", "Private Training", "Group Training", "Not Sure Yet"];

export default function Contact() {
  useSeo({
    title: "Contact | DST New Braunfels — Book Sports Performance, Baseball & Softball Training",
    description: "Contact DST New Braunfels. Call 830-832-9898 or send a message to book strength & conditioning, baseball, and softball training in New Braunfels, TX.",
  });

  const [form, setForm] = useState({ name: "", email: "", phone: "", athlete_age: "", interest: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in your name, email, phone, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", athlete_age: "", interest: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call us at 830-832-9898.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-dst-surface border border-white/15 text-white px-4 py-3.5 outline-none focus:border-dst-red transition-colors placeholder:text-white/30";

  return (
    <>
      <PageHeader
        eyebrow="Contact"
        title={<>Let's Get<br />To Work.</>}
        subtitle="Ready to book or have questions? Call us, or send a message and we'll reach out fast."
      />

      <section className="bg-dst-ink py-20 lg:py-28 grain">
        <div className="container-dst relative z-10 grid lg:grid-cols-[1fr_1.1fr] gap-14">
          {/* Info */}
          <div>
            <Reveal>
              <span className="eyebrow text-dst-red">Get In Touch</span>
              <h2 className="mt-4 font-oswald font-700 uppercase tracking-tighter leading-[0.9] text-4xl lg:text-6xl">
                We're Ready<br />When You Are
              </h2>
            </Reveal>
            <div className="mt-10 space-y-5">
              {[
                { icon: Phone, label: "Call / Text", value: BIZ.phone, href: BIZ.phoneHref },
                { icon: Mail, label: "Email", value: BIZ.email, href: `mailto:${BIZ.email}` },
                { icon: MapPin, label: "Facility", value: `${BIZ.address1}, ${BIZ.address2}`, href: BIZ.maps },
                { icon: Clock, label: "Hours", value: `Daily ${BIZ.hours} · Private training available` },
              ].map((c, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <a href={c.href} target={c.href?.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
                     className="flex items-start gap-4 group">
                    <span className="shrink-0 w-12 h-12 grid place-items-center bg-dst-red/10 text-dst-red group-hover:bg-dst-red group-hover:text-white transition-colors">
                      <c.icon size={20} />
                    </span>
                    <span>
                      <span className="block text-xs tracking-[0.2em] uppercase text-white/40">{c.label}</span>
                      <span className="block text-white group-hover:text-dst-red transition-colors font-600 mt-1">{c.value}</span>
                    </span>
                  </a>
                </Reveal>
              ))}
            </div>
            <div className="flex gap-3 mt-8">
              <a href={BIZ.instagram} target="_blank" rel="noreferrer" className="w-11 h-11 grid place-items-center border border-white/15 text-white/70 hover:bg-dst-red hover:text-white hover:border-dst-red transition-colors"><Instagram size={18} /></a>
              <a href={BIZ.facebook} target="_blank" rel="noreferrer" className="w-11 h-11 grid place-items-center border border-white/15 text-white/70 hover:bg-dst-red hover:text-white hover:border-dst-red transition-colors"><Facebook size={18} /></a>
            </div>
            <a href={BIZ.maps} target="_blank" rel="noreferrer" className="block mt-8 border border-white/10 overflow-hidden">
              <iframe title="map" src="https://www.google.com/maps?q=6700%20I-35%20N%20Frontage%20Rd%20Suite%20106%20New%20Braunfels%20TX%2078130&output=embed" className="w-full h-56 grayscale contrast-125 opacity-80 pointer-events-none" loading="lazy" />
            </a>
          </div>

          {/* Form */}
          <Reveal delay={0.1}>
            <form data-testid="contact-form" onSubmit={submit} className="bg-dst-surface border border-white/10 p-7 lg:p-10">
              <h3 className="font-oswald uppercase text-3xl mb-6">Send A Message</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="contact-name" className={inputCls} placeholder="Full Name *" value={form.name} onChange={set("name")} />
                <input data-testid="contact-phone" className={inputCls} placeholder="Phone *" value={form.phone} onChange={set("phone")} />
                <input data-testid="contact-email" type="email" className={inputCls} placeholder="Email *" value={form.email} onChange={set("email")} />
                <input data-testid="contact-age" className={inputCls} placeholder="Athlete Age" value={form.athlete_age} onChange={set("athlete_age")} />
              </div>
              <select data-testid="contact-interest" className={`${inputCls} mt-4`} value={form.interest} onChange={set("interest")}>
                <option value="">I'm interested in… (optional)</option>
                {INTERESTS.map((o) => <option key={o} value={o} className="bg-dst-surface">{o}</option>)}
              </select>
              <textarea data-testid="contact-message" rows={5} className={`${inputCls} mt-4 resize-none`} placeholder="Tell us about your athlete and goals *" value={form.message} onChange={set("message")} />
              <button data-testid="contact-submit" type="submit" disabled={loading}
                className="btn-dst bg-dst-red text-white hover:bg-white hover:text-dst-ink w-full mt-6 disabled:opacity-60">
                {loading ? "Sending…" : <>Send Message <Send size={18} /></>}
              </button>
              <p className="text-white/40 text-xs mt-4 text-center">Prefer to talk? Call or text {BIZ.phone}.</p>
            </form>
          </Reveal>
        </div>
      </section>
      <CTASection />
    </>
  );
}
